/**
 * Per-section color identity — the single source of truth for a section's
 * accent hue across the whole app.
 *
 * `sectionIntent` (in ./status) buckets a slug into one of the five working
 * intents (info/success/warning/danger/neutral) — good for a quiet status
 * pill, but it can only ever produce five colors, so the nine canonical
 * sections collide and read muddy (design → gray, marketing → "error" red).
 *
 * This module gives every section its OWN vivid hue instead. One hex per
 * slug, picked to walk the color wheel so adjacent rows in the Home rollup
 * stay distinguishable. Everything downstream (banner gradient, progress
 * ring, section pill, rail icon) derives its tints from this one value via
 * `color-mix()` against the mode-aware surface tokens — so the same accent
 * works in light and dark without a second palette.
 *
 * User-created sections (any slug not in the map) hash deterministically
 * into `SECTION_SPECTRUM` so they too get a stable, distinct color.
 *
 * To re-tune a section color globally: edit one row here. Every consumer
 * repaints.
 */

import type { CSSProperties } from "react";

/**
 * Canonical section → accent hex. Ordered as they walk the wheel:
 * indigo → violet → pink (cool/magenta), orange → gold (warm),
 * emerald → teal → cyan (green/blue), slate (neutral bookend).
 */
export const SECTION_COLORS: Record<string, string> = {
  operations: "#6366F1", // indigo — the brand hub
  design: "#8B5CF6", // violet — creative (softer than neon purple)
  marketing: "#DB6C9A", // muted rose — brand energy without hot-pink glare
  development: "#E08A4E", // warm amber — build (calmer than safety-orange)
  finance: "#C79A3E", // deep gold — money (readable, not neon yellow)
  hiring: "#2BA37C", // balanced emerald — people / growth
  "customer-success": "#2E9D9A", // muted teal — care
  sales: "#3D8FC4", // soft blue — deals (deeper than bright cyan)
  legal: "#64748B", // slate — formal / neutral
};

/**
 * Fallback spectrum for user-created sections. Vivid, evenly spaced hues
 * that deliberately skip pure red (reads as an error) and the exact
 * canonical values above so a new section rarely clashes with a built-in.
 */
export const SECTION_SPECTRUM: readonly string[] = [
  "#6366F1", // indigo
  "#8B5CF6", // violet
  "#D946EF", // fuchsia
  "#F43F5E", // rose
  "#F97316", // orange
  "#EAB308", // gold
  "#84CC16", // lime
  "#22C55E", // green
  "#14B8A6", // teal
  "#06B6D4", // cyan
  "#0EA5E9", // sky
  "#3B82F6", // blue
] as const;

/** Cheap, allocation-free, deterministic sum-of-charcodes hash. */
function hashIndex(key: string, mod: number): number {
  let sum = 0;
  for (let i = 0; i < key.length; i++) sum = (sum + key.charCodeAt(i)) | 0;
  return Math.abs(sum) % mod;
}

/**
 * Resolve a section slug to its accent hex. Canonical sections get their
 * pinned color; everything else hashes into the spectrum. Returns a stable
 * neutral for an empty slug.
 */
export function sectionColor(slug: string | null | undefined): string {
  if (!slug) return "#64748B";
  const normalized = slug.toLowerCase();
  return (
    SECTION_COLORS[normalized] ??
    SECTION_SPECTRUM[hashIndex(normalized, SECTION_SPECTRUM.length)]!
  );
}

/**
 * Pick a readable text color (near-black or white) to sit ON a solid fill of
 * the given hex, using the WCAG relative-luminance threshold. Lets a solid
 * accent button stay legible across the whole palette — white on indigo,
 * dark on gold — without a hand-maintained per-section foreground map.
 */
export function readableTextOn(hex: string): string {
  const h = hex.replace("#", "");
  const full =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  const lin = (v: number) =>
    v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  // Crossover ~0.4: keep white on the darker hues (indigo/violet/pink/
  // orange/slate) where it reads well and looks conventional, flip to
  // near-black on the bright mid-tones (emerald/teal/cyan/gold) where white
  // would drop below readable contrast.
  return luminance > 0.4 ? "#0A0A0A" : "#FFFFFF";
}

/**
 * The inline style that publishes a section's accent as the `--section`
 * custom property, plus `--section-fg` — the readable label color to sit ON a
 * solid fill of that accent (so the `section` button variant stays legible
 * across the whole palette: white on indigo, near-black on gold). Spread it
 * onto any element whose subtree wants to derive tints from the section color
 * via `color-mix(in oklab, var(--section) …)` or paint with the accent.
 *
 *   <div style={sectionAccentVars(slug)} className="bg-section-tint">…</div>
 *
 * Typed loosely as CSSProperties so the custom properties pass TS.
 */
export function sectionAccentVars(slug: string | null | undefined): CSSProperties {
  const accent = sectionColor(slug);
  return {
    ["--section" as string]: accent,
    ["--section-fg" as string]: readableTextOn(accent),
  } as CSSProperties;
}
